import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import api from '../../lib/api';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Area,
  AreaChart,
} from 'recharts';
import {
  TrendingUp,
  Users,
  DollarSign,
  Award,
  Crown,
  Star,
  Trophy,
  User,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';

const RANK_COLORS = {
  neg: '#3B82F6',    // Blue
  agent: '#6B7280', // Gray
  rm: '#8B5CF6',    // Purple
  avp: '#EC4899',   // Pink
  svp: '#F97316',   // Orange
  evp: '#EF4444',   // Red
};

const RANK_NAMES = {
  neg: 'Negotiator',
  agent: 'Agent',
  rm: 'Regional Manager',
  avp: 'Asst. VP',
  svp: 'Sr. VP',
  evp: 'Exec. VP',
};

const formatCurrency = (value) => {
  if (value >= 1000000) {
    return `RM ${(value / 1000000).toFixed(2)}M`;
  } else if (value >= 1000) {
    return `RM ${(value / 1000).toFixed(1)}K`;
  }
  return `RM ${value.toFixed(2)}`;
};

const CommissionAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [yearlyBreakdown, setYearlyBreakdown] = useState([]);
  const [rankPerformance, setRankPerformance] = useState([]);
  const [selectedYear, setSelectedYear] = useState('all');

  useEffect(() => {
    fetchAnalyticsData();
  }, []);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      const [overviewRes, yearlyRes, rankRes] = await Promise.all([
        api.get('/commission-analytics/overview'),
        api.get('/commission-analytics/yearly-breakdown'),
        api.get('/commission-analytics/rank-performance'),
      ]);
      
      setOverview(overviewRes.data);
      setYearlyBreakdown(yearlyRes.data);
      setRankPerformance(rankRes.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  // Prepare chart data
  const yearlyTrendData = Object.entries(overview?.yearly_trends || {}).map(([year, data]) => ({
    year,
    total: data.total,
    agents: data.agent_count,
  }));

  const rankDistributionData = (overview?.rank_distribution || []).map(item => ({
    name: RANK_NAMES[item._id] || item._id,
    value: item.count,
    commission: item.total_commission,
    rank: item._id,
  }));

  const pieData = rankDistributionData.map(item => ({
    name: item.name,
    value: item.value,
    fill: RANK_COLORS[item.rank] || '#6B7280',
  }));

  return (
    <div className="space-y-6" data-testid="commission-analytics">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Commission Analytics</h1>
          <p className="text-gray-500">Historical commission data from 2018-2025</p>
        </div>
        <Button onClick={fetchAnalyticsData} variant="outline">
          <TrendingUp className="w-4 h-4 mr-2" />
          Refresh Data
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">Total Agents</p>
                <p className="text-3xl font-bold mt-1">{overview?.summary?.total_agents?.toLocaleString()}</p>
              </div>
              <Users className="w-10 h-10 text-purple-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Total Commission</p>
                <p className="text-3xl font-bold mt-1">
                  {formatCurrency(overview?.summary?.total_commission || 0)}
                </p>
              </div>
              <DollarSign className="w-10 h-10 text-green-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">With Commission</p>
                <p className="text-3xl font-bold mt-1">{overview?.summary?.agents_with_commission?.toLocaleString()}</p>
              </div>
              <Award className="w-10 h-10 text-blue-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm">Avg. Commission</p>
                <p className="text-3xl font-bold mt-1">
                  {formatCurrency(overview?.summary?.average_commission || 0)}
                </p>
              </div>
              <TrendingUp className="w-10 h-10 text-orange-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Yearly Commission Trend */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-600" />
              Yearly Commission Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={yearlyTrendData}>
                <defs>
                  <linearGradient id="colorCommission" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="year" stroke="#6B7280" />
                <YAxis 
                  stroke="#6B7280"
                  tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                />
                <Tooltip 
                  formatter={(value) => [`RM ${value.toLocaleString()}`, 'Commission']}
                  labelFormatter={(label) => `Year ${label}`}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #E5E7EB' }}
                />
                <Area 
                  type="monotone" 
                  dataKey="total" 
                  stroke="#8B5CF6" 
                  fillOpacity={1} 
                  fill="url(#colorCommission)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Rank Distribution Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Agent Distribution by Rank
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value) => [value, 'Agents']}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #E5E7EB' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Commission by Rank */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              Total Commission by Rank
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={rankDistributionData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis 
                  type="number" 
                  stroke="#6B7280"
                  tickFormatter={(value) => `${(value / 1000000).toFixed(1)}M`}
                />
                <YAxis type="category" dataKey="name" stroke="#6B7280" width={100} />
                <Tooltip 
                  formatter={(value) => [`RM ${value.toLocaleString()}`, 'Commission']}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #E5E7EB' }}
                />
                <Bar dataKey="commission" radius={[0, 4, 4, 0]}>
                  {rankDistributionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={RANK_COLORS[entry.rank] || '#6B7280'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Rank Performance Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-600" />
              Rank Performance Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {rankPerformance.map((rank) => (
                <div key={rank.rank} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-3 h-10 rounded-full"
                      style={{ backgroundColor: RANK_COLORS[rank.rank] }}
                    />
                    <div>
                      <p className="font-medium text-gray-900">{rank.rank_name}</p>
                      <p className="text-sm text-gray-500">{rank.agent_count} agents</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-green-600">
                      {formatCurrency(rank.total_commission)}
                    </p>
                    <p className="text-xs text-gray-500">
                      Avg: {formatCurrency(rank.avg_commission)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Earners */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Crown className="w-5 h-5 text-yellow-500" />
            Top 10 Commission Earners
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {overview?.top_earners?.map((agent, index) => (
              <div 
                key={agent.id}
                className={`relative p-4 rounded-xl border ${
                  index === 0 ? 'bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-200' :
                  index === 1 ? 'bg-gradient-to-br from-gray-50 to-slate-100 border-gray-300' :
                  index === 2 ? 'bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200' :
                  'bg-white border-gray-200'
                }`}
              >
                <div className="absolute -top-2 -left-2 w-8 h-8 rounded-full bg-white shadow-md flex items-center justify-center">
                  <span className={`font-bold ${
                    index === 0 ? 'text-yellow-600' :
                    index === 1 ? 'text-gray-500' :
                    index === 2 ? 'text-orange-600' :
                    'text-gray-400'
                  }`}>
                    #{index + 1}
                  </span>
                </div>
                <div className="pt-2">
                  <p className="font-semibold text-gray-900 truncate">{agent.full_name}</p>
                  <p className="text-xs text-gray-500">{agent.agent_code}</p>
                  <span 
                    className="inline-block mt-2 px-2 py-0.5 rounded text-xs font-medium"
                    style={{ 
                      backgroundColor: `${RANK_COLORS[agent.role]}20`,
                      color: RANK_COLORS[agent.role]
                    }}
                  >
                    {RANK_NAMES[agent.role] || agent.role}
                  </span>
                  <p className="mt-2 text-lg font-bold text-green-600">
                    {formatCurrency(agent.total_commission_earned)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Yearly Breakdown Detail */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-purple-600" />
            Yearly Commission Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={yearlyBreakdown}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="year" stroke="#6B7280" />
              <YAxis 
                stroke="#6B7280"
                tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
              />
              <Tooltip 
                formatter={(value) => [`RM ${value?.toLocaleString() || 0}`, 'Commission']}
                contentStyle={{ borderRadius: '8px', border: '1px solid #E5E7EB' }}
              />
              <Legend />
              <Bar dataKey="total" name="Total Commission" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
};

export default CommissionAnalytics;
